import { useCallback, useEffect, useRef, useState } from 'react'

export type ImagePickError = 'not_image' | 'too_large'

/**
 * File input → validation → object URL for an `ImageCropDialog`. Wire
 * `inputRef` + `onFileChange` to a hidden `<input type="file">`, open it with
 * `openPicker`, pass `cropSrc` to the dialog and call `closeCrop` on cancel or
 * after the cropped blob is handled. The object URL is revoked whenever it's
 * replaced or cleared, and on unmount.
 */
export function useImagePickCrop({
  maxBytes = Infinity,
  onInvalid,
}: { maxBytes?: number; onInvalid?: (reason: ImagePickError) => void } = {}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [cropSrc, setCropSrc] = useState<string | null>(null)

  useEffect(() => {
    if (!cropSrc) return
    return () => URL.revokeObjectURL(cropSrc)
  }, [cropSrc])

  const openPicker = useCallback(() => inputRef.current?.click(), [])
  const closeCrop = useCallback(() => setCropSrc(null), [])

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Reset so picking the same file again still fires `change`.
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) return onInvalid?.('not_image')
    if (file.size > maxBytes) return onInvalid?.('too_large')
    setCropSrc(URL.createObjectURL(file))
  }

  return { inputRef, openPicker, onFileChange, cropSrc, closeCrop }
}
